import type RolesRepository from "@/core/administration/repositories/RolesRepository";
import {
  CreateRoleRequest,
  CreateRoleResponse,
  Role,
  RoleResponse,
} from "@/core/administration/entities/Roles";
import {
  RepositoryResult,
  RepositoryResultFactory,
} from "@/core/shared/types/RepositoryResult";
import { HttpClient } from "../../http/HttpClient";
import { ApiResponse } from "../../api/types/ApiResponse";
import { ApiResponseHandler } from "../../http/services/handleApiResponse/handleApiResponse";
import RolesMapper, { ApiRoleItem } from "@/core/customers/mappers/RolesMapper";
import handleRequestError from "../../http/services/handleRequestError/handleRequestError";
import {
  createRoleUrl,
  getRolesUrl,
} from "../../api/rolesBackendEndpoints/rolesBackendEndpoints";
import CreateRoleMapper, {
  ApiCreateRoleBody,
  ApiCreatedRoleData,
} from "@/core/administration/mappers/CreateRoleMapper";

export default class RolesRepositoryBackend implements RolesRepository {
  constructor(private readonly httpClient: HttpClient) {}
  async getRoles(
    page: number,
    size: number,
  ): Promise<RepositoryResult<RoleResponse>> {
    try {
      const url = getRolesUrl(page, size);

      const response = await this.httpClient.get<
        ApiResponse<{ items: ApiRoleItem[]; totalElements: number }>
      >({
        url,
        withAuth: true,
      });

      const result = ApiResponseHandler.handleResponse<{
        items: ApiRoleItem[];
        totalElements: number;
      }>(response);

      if (result.success) {
        // Ensure totalElements is properly converted to a number
        // Handle cases where it might be a string or undefined
        const rawTotalElements = result.data?.totalElements;
        const totalElements =
          typeof rawTotalElements === "number"
            ? rawTotalElements
            : typeof rawTotalElements === "string"
              ? parseInt(rawTotalElements, 10) || 0
              : 0;

        const mapped: RoleResponse = {
          code: response.code,
          message: response.message,
          data: result.data
            ? {
                items: RolesMapper.toDomainItems(
                  (result.data.items as ApiRoleItem[]) || [],
                ),
                totalElements,
              }
            : { items: [], totalElements: 0 },
        };
        return RepositoryResultFactory.success(mapped);
      }

      return RepositoryResultFactory.error(
        ApiResponseHandler.createExceptionFromResponse(response),
      );
    } catch (error) {
      return RepositoryResultFactory.error(
        handleRequestError(error as ApiResponse<unknown>),
      );
    }
  }

  async createRole(
    payload: CreateRoleRequest,
  ): Promise<RepositoryResult<CreateRoleResponse>> {
    try {
      const url = createRoleUrl();

      console.log("payload", payload);
      console.log("mapping", CreateRoleMapper.toApiRequest(payload));

      const response = await this.httpClient.post<
        ApiResponse<ApiCreatedRoleData>,
        ApiCreateRoleBody
      >({
        url,
        body: CreateRoleMapper.toApiRequest(payload),
        withAuth: true,
      });

      const result =
        ApiResponseHandler.handleResponse<ApiCreatedRoleData>(response);

      if (result.success && result.data) {
        const createdRole: Role = CreateRoleMapper.toDomainRole(
          result.data,
          payload,
        );

        const dto: CreateRoleResponse = {
          code: response.code,
          message: response.message,
          data: createdRole,
        };

        return RepositoryResultFactory.success(dto);
      }

      return RepositoryResultFactory.error(
        ApiResponseHandler.createExceptionFromResponse(response),
      );
    } catch (error) {
      return RepositoryResultFactory.error(
        handleRequestError(error as ApiResponse<unknown>),
      );
    }
  }
}
